import axios from 'axios';
import { checkCookie, clearCookie, getCsrfTokenName, isUserCookie, removeCsrfCookie, setUserCookie } from './cookie';
import { apiBaseUrl } from './runtimeConfig';
import * as Sentry from "@sentry/react";
import { formatRequest, formatResponse } from './common';
import Swal from 'sweetalert2';
import { LOB_MODULE } from '../constants';
import { clearLicenseState, resolveLicenseBlockedMessage, setLicenseState } from './licenseHealth';
import { getStoredModuleHeader, setStoredModule } from './module';
import { markAppError, requestSettled, requestStarted } from './readyFlag';

const LICENSE_BLOCKED_FLAG = 'license_blocked';
let isHandlingLicenseBlock = false;

function isGermanLanguage() {
  const lang = (localStorage.getItem('preferred_lang') || 'de').toLowerCase();
  return lang.startsWith('de');
}

function getLocalizedText(englishText, germanText) {
  return isGermanLanguage() ? germanText : englishText;
}

function getLicenseBlockedMessage(serverMessage = null) {
  return resolveLicenseBlockedMessage(
    serverMessage,
    localStorage.getItem('preferred_lang') || 'de'
  );
}

function getNetworkErrorCopy() {
  const isGerman = isGermanLanguage();
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (isOffline) {
    return isGerman
      ? {
        title: 'Keine Internetverbindung',
        message: 'Es wurde keine Internetverbindung erkannt. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      }
      : {
        title: 'No Internet Connection',
        message: 'No internet connection detected. Please check your connection and try again.',
      };
  }

  return isGerman
    ? {
      title: 'Anwendung nicht erreichbar',
      message: 'Die Anwendung konnte nicht erreicht werden. Dies kann an einem Netzwerkproblem oder an einem laufenden Anwendungsupdate liegen. Bitte versuchen Sie es in wenigen Augenblicken erneut.',
    }
    : {
      title: 'Unable to Reach Application',
      message: "We couldn't reach the application. This may be due to a network issue or application update in progress. Please try again in a few moments.",
    };
}

const instance = axios.create({
  baseURL: `${apiBaseUrl}/api/v1`,
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: getCsrfTokenName(),
  xsrfHeaderName: 'X-XSRF-TOKEN'
});

// Counted for the ready flag. Anything polled in the background opts out with
// `meta: { background: true }`, otherwise the counter never settles and the flag
// never appears.
function isCounted(config) {
  return !config?.meta?.background;
}

instance.interceptors.request.use(async function (config) {
  const counted = isCounted(config);
  if (counted) requestStarted();

  try {
    return await buildRequest(config);
  } catch (err) {
    // A rejection here still reaches the response error handler below, so mark it
    // settled or the counter is decremented twice for one request.
    if (counted) requestSettled();
    if (err) err.__readySettled = true;
    throw err;
  }
});

async function buildRequest(config) {
  if (sessionStorage.getItem(LICENSE_BLOCKED_FLAG) === '1') {
    const isLoginPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/to-login');
    if (!isLoginPage) {
      const blockedError = new Error(getLocalizedText(
        'License access is blocked. Redirecting to login.',
        'Der Lizenzzugriff ist blockiert. Weiterleitung zur Anmeldung.'
      ));
      blockedError.response = {
        status: 402,
        data: {
          message: getLicenseBlockedMessage(),
        },
      };
      throw blockedError;
    }
  }

  config.headers = config.headers || {};
  config.headers['X-Tenant'] = localStorage.getItem('accountId');
  config.headers['X-Period'] = localStorage.getItem('period');
  config.headers['X-Module'] = getStoredModuleHeader(LOB_MODULE);

  if (!checkCookie(getCsrfTokenName())) await getCsrfToken();

  if (config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase()) && config.data && !(config.data instanceof FormData)) {
    config.data = formatRequest(structuredClone(config.data));
  }

  return config;
}

instance.interceptors.response.use(function (response) {
  if (isCounted(response.config)) requestSettled();

  if (!localStorage.getItem('period') && response.headers['x-period']) {
    localStorage.setItem('period', response.headers['x-period']);
  }

  if (response.headers['x-module'] || response.headers['x-mode']) {
    setStoredModule(response.headers['x-module'] || response.headers['x-mode']);
  }

  if (isUserCookie()) setUserCookie(); // extend user cookie expires

  if (response.headers['content-type'] === 'application/json') {
    response.data = formatResponse(response.data);
  }

  return response;
}, async function (error) {
  // Released before the retry below re-enters the request interceptor, so a
  // retried request counts once per attempt rather than stacking.
  if (!error?.__readySettled && isCounted(error.config)) requestSettled();

  const status = error.response?.status;
  if (!error.response || status >= 500) markAppError();

  // in case of CSRF token mismatch: remove XSRF-TOKEN token from parent cookie
  if (error.response) {
    // A non-2xx body is not always JSON: nginx/maintenance pages return an HTML
    // string and blob-response requests return a Blob. Assigning `.message` on a
    // string primitive throws in strict mode, so normalise those away first.
    // Blobs stay untouched, callers read them with `data.text()`.
    if (typeof error.response.data !== 'object' || error.response.data === null) {
      error.response.data = {};
    }

    const requestId = typeof error.response.headers?.get === 'function'
      ? error.response.headers.get('x-request-id')
      : error.response.headers?.['x-request-id'];
    Sentry.addBreadcrumb({
      category: 'http',
      message: `X-Request-Id: ${requestId || 'n/a'}`,
      level: 'error'
    });

    if (error.response?.status === 419) {
      removeCsrfCookie();
      error.response.data.message = (error.response.data.message || '') + getLocalizedText(' Please try again.', ' Bitte versuchen Sie es erneut.');
    }

    if (
      error.response?.data?.translation_key === 'message.error.invalid_tenant' ||
      error.response?.data?.message?.toLowerCase().includes('invalid tenant')
    ) {
      error.response.data.message = getLocalizedText(
        'Invalid Account ID. Please provide a valid Account ID and try again.',
        'Ungültige Account-ID. Bitte geben Sie eine gültige Account-ID ein und versuchen Sie es erneut.'
      );
    }

    // in case of 503: return in maintenance mode
    if (error.response?.status === 503) {
      error.message = getLocalizedText(
        'The application is currently in maintenance mode. Please try again later.',
        'Die Anwendung befindet sich derzeit im Wartungsmodus. Bitte versuchen Sie es später erneut.'
      );
      error.response.data.message = error.message;
    }

    // in case of 402: fail fast on license blocking
    if (error.response?.status === 402) {
      const message = getLicenseBlockedMessage(error.response?.data?.message);
      error.message = message;
      error.response.data = error.response.data || {};
      error.response.data.message = message;
      setLicenseState({
        status: 'blocked',
        warningCode: error.response?.data?.code || 'license_blocked',
        warningMessage: message,
        warningCount: 1,
        updatedAt: new Date().toISOString(),
      });

      sessionStorage.setItem(LICENSE_BLOCKED_FLAG, '1');

      if (!isHandlingLicenseBlock) {
        isHandlingLicenseBlock = true;
        clearCookie();
        Sentry.setUser(null);
        Sentry.setContext('tenant', null);
        localStorage.removeItem('period');
        localStorage.removeItem('module');
        localStorage.removeItem('mode_scope');

        Swal.fire({
          icon: "error",
          title: getLocalizedText('License Required', 'Lizenz erforderlich'),
          html: message,
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            confirmButton: 'btn-primary'
          }
        }).finally(() => {
          const isLoginPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/to-login');
          if (!isLoginPage) {
            window.location.href = '/login';
          } else {
            isHandlingLicenseBlock = false;
          }
        });
      }
    }

    // in case of 403 on the period or the role as a whole: leave the page instead of failing every request on it.
    // Other 403s are left to the caller.
    if (error.response?.status === 403) {
      const translationKey = error.response?.data?.translation_key;

      if (translationKey === 'message.error.period_not_available') {
        // the stored period is sent with every request, so drop it or the selector fails the same way
        localStorage.removeItem('period');
        if (!window.location.pathname.includes('/period-selector')) {
          window.location.href = '/period-selector';
        }
      }

      if (translationKey === 'message.error.no_effective_role' && !window.location.pathname.includes('/profile-settings')) {
        window.location.href = '/profile-settings';
      }
    }

    // in case of 410: logout the user and redirect to login page
    if (error.response?.status === 410 || error.response?.status === 401) {
      clearCookie();
      Sentry.setUser(null);
      Sentry.setContext('tenant', null);
      localStorage.removeItem('period');
      sessionStorage.removeItem(LICENSE_BLOCKED_FLAG);
      clearLicenseState();

      // redirect ot login if not in login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  } else if (error.request) {
    // The request was made but no response was received. probably because of network error, CORS issue, or the server is down, deploying, etc.
    const config = error.config || {};
    const method = (config.method || 'get').toLowerCase();
    const isIdempotent = ['get', 'head', 'options'].includes(method);
    config.__retryCount = config.__retryCount || 0;

    if (isIdempotent && config.__retryCount < 3) {
      config.__retryCount += 1;
      await delay(1000 * config.__retryCount); // simple backoff per attempt
      return instance(config);
    }

    const networkErrorCopy = getNetworkErrorCopy();
    error.message = networkErrorCopy.message;
    error.response = {
      data: {
        message: error.message
      }
    };

    Swal.fire({
      icon: "warning",
      title: networkErrorCopy.title,
      html: `${error.message}`,
      customClass: {
        confirmButton: 'btn-primary'
      }
    });
  } else {
    // Something happened in setting up the request that triggered an Error
    error.message = error.message || (
      isGermanLanguage()
        ? 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
        : 'An unexpected error occurred. Please try again.'
    );
    error.response = {
      data: {
        message: error.message
      }
    };
  }

  return Promise.reject(error);
});

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function getCsrfToken() {
  try {
    await axios.get(
      `${apiBaseUrl}/sanctum/csrf-cookie`,
      {
        withCredentials: true,
        withXSRFToken: true,
        xsrfCookieName: getCsrfTokenName(),
        xsrfHeaderName: 'X-XSRF-TOKEN',
        headers: {
          'X-Tenant': localStorage.getItem('accountId'),
          'X-Period': localStorage.getItem('period'),
        }
      }
    );
    sessionStorage.removeItem(LICENSE_BLOCKED_FLAG);
  } catch (error) {
    if (error?.response?.status === 503) {
      error.message = getLocalizedText(
        'The application is currently in maintenance mode. Please try again later.',
        'Die Anwendung befindet sich derzeit im Wartungsmodus. Bitte versuchen Sie es später erneut.'
      );
    }

    Sentry.captureException(error);
  }
}

export { axios, instance as api, getCsrfToken };
